import React, { useContext, useEffect, useLayoutEffect, useRef } from 'react'
import './PeakLevelMeter.css'
import { PEAK_SCALE } from '../constants/visualizer'
import {
  deliveredStereoChannels,
  stereoInputConstraints,
} from '../audio/stereoInput'
import { SharedInputContext, type SharedInput } from '../audio/sharedInput'

interface PeakLevelMeterProps {
  isListening: boolean
  selectedDeviceId: string
  isStereo: boolean
  isFullscreen?: boolean
  scaleCoefficient?: number
}

const levels = [6, 4, 2, 0, -2, -4, -6, -10, -15, -20] // From top (+6) to bottom (-20)

const maskForLevel = (db: number) => {
  let mask = 0
  for (let i = 0; i < levels.length; i++) {
    if (db >= levels[i]) mask |= 1 << i
  }
  return mask
}

const paintLeds = (
  leds: Array<HTMLDivElement | null>,
  mask: number
) => {
  for (let i = 0; i < levels.length; i++) {
    leds[i]?.classList.toggle('active', (mask & (1 << i)) !== 0)
  }
}

const PeakLevelMeter: React.FC<PeakLevelMeterProps> = ({
  isListening,
  selectedDeviceId,
  isStereo,
  isFullscreen = false,
  scaleCoefficient = 1.0,
}) => {
  const sharedInput = useContext(SharedInputContext)
  const leftLedsRef = useRef<Array<HTMLDivElement | null>>([])
  const rightLedsRef = useRef<Array<HTMLDivElement | null>>([])
  const leftMaskRef = useRef(0)
  const rightMaskRef = useRef(0)
  const scaleCoefficientRef = useRef(scaleCoefficient)

  useEffect(() => {
    scaleCoefficientRef.current = scaleCoefficient
  }, [scaleCoefficient])

  const applyMasks = () => {
    paintLeds(leftLedsRef.current, leftMaskRef.current)
    paintLeds(rightLedsRef.current, rightMaskRef.current)
  }

  // A parent render replaces className and would otherwise drop "active".
  useLayoutEffect(() => {
    applyMasks()
  })

  useLayoutEffect(() => {
    let isActive = true
    let animationFrameId: number | null = null
    let retained: SharedInput | null = null
    let source: MediaStreamAudioSourceNode | null = null
    let feed: AudioNode | null = null
    let tap: AudioNode | null = null
    let splitter: ChannelSplitterNode | null = null
    let analyserL: AnalyserNode | null = null
    let analyserR: AnalyserNode | null = null
    let ownedContext: AudioContext | null = null
    let ownedStream: MediaStream | null = null
    let unsubscribeFrame: (() => void) | null = null
    let sharedSplitter = false

    const clearMeters = () => {
      leftMaskRef.current = 0
      rightMaskRef.current = 0
      applyMasks()
    }

    const stopLoop = () => {
      isActive = false
      unsubscribeFrame?.()
      unsubscribeFrame = null
      if (animationFrameId !== null) cancelAnimationFrame(animationFrameId)
      if (feed && tap) {
        try {
          feed.disconnect(tap)
        } catch {
          // The context was already closed.
        }
      }
      if (sharedSplitter && splitter) {
        if (analyserL) {
          try {
            splitter.disconnect(analyserL)
          } catch {
            // Already disconnected.
          }
        }
        if (analyserR) {
          try {
            splitter.disconnect(analyserR)
          } catch {
            // Already disconnected.
          }
        }
      } else {
        splitter?.disconnect()
      }
      analyserL?.disconnect()
      analyserR?.disconnect()
      if (ownedContext) {
        source?.disconnect()
        ownedStream?.getTracks().forEach((track) => track.stop())
        if (ownedContext.state !== 'closed') ownedContext.close().catch(() => {})
      }
      retained?.release()
      retained = null
      clearMeters()
    }

    if (!isListening) {
      clearMeters()
      return
    }

    // The deck is listening, but its single capture is not ready yet.
    if (sharedInput === null) {
      clearMeters()
      return
    }

    const startLoop = (
      context: AudioContext,
      input: MediaStreamAudioSourceNode,
      deliveredChannels: number,
      shared?: SharedInput
    ) => {
      if (shared?.scaleRef) {
        scaleCoefficientRef.current = shared.scaleRef.current
      }
      const reuseSplitter =
        shared?.splitter && isStereo && deliveredChannels >= 2
          ? shared.splitter
          : null
      sharedSplitter = Boolean(reuseSplitter)
      if (reuseSplitter) {
        splitter = reuseSplitter
        analyserL = context.createAnalyser()
        analyserL.fftSize = 1024
        splitter.connect(analyserL, 0)
        analyserR = context.createAnalyser()
        analyserR.fftSize = 1024
        splitter.connect(analyserR, 1)
        tap = analyserL
        feed = splitter
      } else if (isStereo && deliveredChannels >= 2) {
        splitter = context.createChannelSplitter(2)
        analyserL = context.createAnalyser()
        analyserL.fftSize = 1024
        splitter.connect(analyserL, 0)
        analyserR = context.createAnalyser()
        analyserR.fftSize = 1024
        splitter.connect(analyserR, 1)
        tap = splitter
        feed = input
        input.connect(tap)
      } else {
        analyserL = context.createAnalyser()
        analyserL.fftSize = 1024
        analyserR = null
        tap = analyserL
        feed = input
        input.connect(tap)
      }

      const dataArrayL = new Float32Array(analyserL.fftSize) as Float32Array<ArrayBuffer>
      const dataArrayR = analyserR
        ? (new Float32Array(analyserR.fftSize) as Float32Array<ArrayBuffer>)
        : null

      const computeDB = (analyser: AnalyserNode, dataArray: Float32Array<ArrayBuffer>) => {
        analyser.getFloatTimeDomainData(dataArray)
        let peak = 0
        for (let i = 0; i < dataArray.length; i++) {
          const abs = Math.abs(dataArray[i])
          if (abs > peak) peak = abs
        }
        const boost = shared?.scaleRef.current ?? scaleCoefficientRef.current
        const scaledPeak = peak * PEAK_SCALE * boost
        return scaledPeak > 0 ? 20 * Math.log10(scaledPeak) : -Infinity
      }

      const updateLevels = () => {
        if (!isActive || context.state === 'closed') return
        if (!analyserL) return

        const leftDB = computeDB(analyserL, dataArrayL)
        const leftMask = maskForLevel(leftDB)
        if (leftMask !== leftMaskRef.current) {
          leftMaskRef.current = leftMask
          paintLeds(leftLedsRef.current, leftMask)
        }

        let rightMask = 0
        if (isStereo) {
          const rightDB =
            analyserR && dataArrayR
              ? computeDB(analyserR, dataArrayR)
              : leftDB
          rightMask = maskForLevel(rightDB)
        }
        if (rightMask !== rightMaskRef.current) {
          rightMaskRef.current = rightMask
          paintLeds(rightLedsRef.current, rightMask)
        }
      }

      if (shared) {
        unsubscribeFrame = shared.subscribeFrame(updateLevels)
        return
      }

      const loop = () => {
        updateLevels()
        animationFrameId = requestAnimationFrame(loop)
      }
      loop()
    }

    if (sharedInput) {
      if (sharedInput.context.state === 'closed') return
      sharedInput.retain()
      retained = sharedInput
      startLoop(
        sharedInput.context,
        sharedInput.source,
        sharedInput.deliveredChannels,
        sharedInput
      )
      return stopLoop
    }

    const setupOwnInput = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: stereoInputConstraints(selectedDeviceId),
        })
        if (!isActive) {
          stream.getTracks().forEach((track) => track.stop())
          return
        }
        const deliveredChannels = await deliveredStereoChannels(
          stream.getAudioTracks()[0]
        )
        if (!isActive) {
          stream.getTracks().forEach((track) => track.stop())
          return
        }
        ownedStream = stream
        ownedContext = new AudioContext()
        if (ownedContext.state === 'suspended') {
          await ownedContext.resume().catch(() => {})
        }
        if (!isActive) {
          stream.getTracks().forEach((track) => track.stop())
          ownedContext.close().catch(() => {})
          return
        }
        source = ownedContext.createMediaStreamSource(stream)
        startLoop(ownedContext, source, deliveredChannels)
      } catch (error) {
        if (isActive) {
          console.error('Error setting up audio for peak meter:', error)
        }
      }
    }

    setupOwnInput()
    return stopLoop
  }, [isListening, selectedDeviceId, isStereo, sharedInput])

  const getLedClass = (db: number) => {
    if (db === 0) return 'zero'
    if (db > 0) return 'positive'
    return 'negative'
  }

  return (
    <div className={`peak-level-meter ${isFullscreen ? 'fullscreen' : ''}`}>
      <div className="title">PEAK LEVEL</div>
      <div className="channels">
        <div className="channel left">
          {levels.map((db, index) => (
            <div
              key={`left-${db}`}
              ref={(node) => {
                leftLedsRef.current[index] = node
              }}
              className={`led ${getLedClass(db)}`}
            />
          ))}
        </div>
        <div className="db-labels">
          {levels.map((db) => (
            <span key={db}>{db > 0 ? `+${db}` : db}</span>
          ))}
        </div>
        {isStereo && (
          <div className="channel right">
            {levels.map((db, index) => (
              <div
                key={`right-${db}`}
                ref={(node) => {
                  rightLedsRef.current[index] = node
                }}
                className={`led ${getLedClass(db)}`}
              />
            ))}
          </div>
        )}
      </div>
      <div className="bottom-labels">
        <span>L</span>
        <span>dB</span>
        {isStereo && <span>R</span>}
      </div>
    </div>
  )
}

export default PeakLevelMeter
